'use client';
import { useMemo, useState } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';

import { FormToggleButtonRadioGroup } from '@ui/uikit/components/common/FormToggleButtonRadioGroup';

import { useRounds } from '../../model/web3Raffle/api/useRounds';
import { ViewMyTicketsModal } from '../modals/ViewMyTicketsButton/ViewMyTicketsModal';
import { EntriesTable } from './EntriesTable';

export const options: { value: TabName; label: string }[] = [
  {
    value: 'all',
    label: 'All Entries',
  },
  {
    value: 'wins',
    label: 'Wins Only',
  },
];

type TabName = 'all' | 'wins';

const ROWS_PER_PAGE = 5;

export const MagicRaffleEntries = () => {
  const { userEvmWallet } = useAuth();

  const { data: rounds = [], isPending: isMyTicketsLoading } = useRounds(
    userEvmWallet?.attributes?.address,
  );
  const [selectedTab, setSelectedTab] = useState<TabName>('all');

  const filteredRounds = useMemo(() => {
    return selectedTab === 'all' ? rounds : rounds.filter((r) => r.prizeInSqr.value);
  }, [selectedTab, rounds]);
  const [currentPage, setCurrentPage] = useState(0);
  const displayedRounds = useMemo(() => {
    return filteredRounds.slice(0, (currentPage + 1) * ROWS_PER_PAGE);
  }, [filteredRounds, currentPage]);

  const onClickShowMore = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const isShowMoreVisible =
    filteredRounds?.length > ROWS_PER_PAGE && filteredRounds.length > displayedRounds?.length;

  const onClickTabHandler = (tab: TabName) => {
    setSelectedTab(tab);
    setCurrentPage(0);
  };

  const [clickedRoundId, setClickedRoundId] = useState<bigint>(BigInt(0));

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onClickRoundHandler = (roundId: bigint) => {
    setClickedRoundId(roundId);
    setIsModalOpen(true);
  };

  const closeModalHandler = () => {
    setIsModalOpen(false);
  };

  const restPagesCount = filteredRounds?.length - displayedRounds?.length;

  return (
    userEvmWallet?.attributes?.address && (
      <>
        <div>
          <div className="flex flex-col sm:flex-row justify-between items-start md:items-center mb-8">
            <h2 className="text-h3-a mb-4 md:mb-0">My Raffle Entries</h2>
            <FormToggleButtonRadioGroup
              wrapperClassName="max-sm-msq:w-full"
              className="w-full md:w-auto bg-base-bg-secondary text-base-text-tertiary rounded-lg"
              options={options}
              value={selectedTab}
              onValueChange={onClickTabHandler}
            />
          </div>
          <EntriesTable
            onClickRow={onClickRoundHandler}
            rounds={displayedRounds}
            isLoading={isMyTicketsLoading}
            isEmpty={!isMyTicketsLoading && !displayedRounds?.length}
          />
          {isShowMoreVisible && (
            <button
              className="msq-btn msq-btn-secondary msq-btn-md mt-6 max-sm-msq:w-full"
              onClick={onClickShowMore}
              disabled={isMyTicketsLoading}
            >
              Show Next {Math.min(ROWS_PER_PAGE, restPagesCount)}
            </button>
          )}
        </div>
        <ViewMyTicketsModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          closeModal={closeModalHandler}
          roundId={clickedRoundId}
        />
      </>
    )
  );
};
